/**
 * RoomBudgetGauge — the room's consumed/allocated budget shown as a thin
 * HORIZONTAL bar that lines up with the room-header's bottom border
 * (USAGE-AND-BUDGET.md §5b/§7b). The fill width = fraction of allocated points
 * used this month; color transitions green → amber (80%) → red (95%+), matching
 * the soft-warning thresholds. Renders nothing when the room has no allocation.
 *
 * Two render modes:
 *   - default ("inline"): the bar + a "consumed/allocated pts" label, for use
 *     beside the room name.
 *   - ``variant="border"``: just the full-width bar, meant to be absolutely
 *     positioned on the header's bottom edge so the room-name underline itself
 *     becomes the budget indicator.
 */
import React, { useEffect, useState } from 'react';
import { getRoomBudget } from '@/services/budgetApi';

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

function barColor(pct) {
  if (pct >= 95) return '#dc2626'; // red-600
  if (pct >= 80) return '#d97706'; // amber-600
  return '#059669'; // emerald-600
}

export function useRoomBudget(networkId, poolId) {
  const [b, setB] = useState(null);
  useEffect(() => {
    let active = true;
    if (!networkId || !poolId) { setB(null); return undefined; }
    getRoomBudget(networkId, poolId)
      .then((res) => { if (active) setB(res); })
      .catch(() => { if (active) setB(null); });
    return () => { active = false; };
  }, [networkId, poolId]);
  return b;
}

export default function RoomBudgetGauge({ networkId, poolId, variant = 'inline' }) {
  const b = useRoomBudget(networkId, poolId);
  if (!b) return null; // unallocated room or load failure → nothing to show

  const pct = Math.max(0, Math.min(100, Number(b.pct) || 0));
  const color = barColor(b.pct);
  const title = `Room budget: ${fmt(b.consumed)} / ${fmt(b.allocated)} pts (${b.pct}%) this month`;

  // Border mode — a full-width 2px bar; the parent positions it on the header's
  // bottom edge (over the gray border). The unfilled remainder stays transparent
  // so the gray border shows through as the "track".
  if (variant === 'border') {
    // A faint tinted track makes the gauge visible even at 0% (otherwise it's
    // indistinguishable from the plain gray header border). The solid fill sits
    // on top; a 0%-but-allocated room still shows a thin colored hairline so the
    // user knows the budget bar exists.
    const fillPct = pct > 0 ? Math.max(pct, 2) : 0; // tiny floor so low % is visible
    return (
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px]"
        style={{ backgroundColor: `${color}33` }}  /* ~20% opacity track */
        data-testid="room-budget-bar"
        title={title}
        aria-label={title}
      >
        <div
          className="h-full transition-[width] duration-500"
          style={{ width: `${fillPct}%`, backgroundColor: color }}
        />
      </div>
    );
  }

  // Inline mode — a compact bar + label beside the room name.
  return (
    <div
      className="inline-flex items-center gap-1.5"
      title={title}
      data-testid="room-budget-gauge"
    >
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[10px] leading-tight text-gray-500">
        {b.pct}% · {fmt(b.consumed)}/{fmt(b.allocated)} pts
      </span>
    </div>
  );
}
